import { useEffect, useState } from 'react';

export function Modal({ title, onClose, children, wide }) {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={'modal' + (wide ? ' modal-wide' : '')} role="dialog" aria-modal="true">
        <div className="modal-head">
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Fermer">✕</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

export function youtubeEmbed(url) {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  return m ? `https://www.youtube-nocookie.com/embed/${m[1]}` : null;
}

export function VideoPlayer({ url }) {
  if (!url) return null;
  const embed = youtubeEmbed(url);
  if (embed) {
    return (
      <div className="video-frame">
        <iframe src={embed} title="Vidéo de l'exercice" allowFullScreen loading="lazy" />
      </div>
    );
  }
  return <video className="video-el" src={url} controls preload="metadata" playsInline />;
}

export function Thumb({ src, alt, size = 48 }) {
  if (!src) return <div className="thumb thumb-empty" style={{ width: size, height: size }}>💪</div>;
  return <img className="thumb" src={src} alt={alt || ''} loading="lazy" style={{ width: size, height: size }} />;
}

// Animation du mouvement : fondu enchaîné entre la pose de départ et de fin ; clic/toucher = lecture/pause
export function ImagePair({ start, end, alt }) {
  const [playing, setPlaying] = useState(true);
  if (!start && !end) return null;
  if (!start || !end) {
    return (
      <div className="exo-anim">
        <img className="exo-anim-frame" src={start || end} alt={alt || ''} />
      </div>
    );
  }
  const toggle = () => setPlaying((p) => !p);
  return (
    <button
      type="button"
      className={'exo-anim' + (playing ? ' is-playing' : '')}
      onClick={toggle}
      aria-label={`${alt || 'Exercice'} : ${playing ? 'mettre l’animation en pause' : 'lancer l’animation'}`}
    >
      <img className="exo-anim-frame" src={start} alt={`${alt || ''} – départ`} />
      <img className="exo-anim-frame exo-anim-top" src={end} alt={`${alt || ''} – fin`} />
      <span className="exo-anim-ctl" aria-hidden="true">{playing ? '⏸' : '▶'}</span>
    </button>
  );
}

const round = (n) => Math.round(n * 100) / 100;

// Champ numérique avec boutons −/+ ; `base` sert de point de départ quand le champ est vide (ex. dernière perf)
export function Stepper({ value, onChange, step = 1, min = 0, base, placeholder, ariaLabel, decimal = false }) {
  const bump = (dir) => {
    const from = value ?? base ?? 0;
    onChange(Math.max(min, round(from + dir * step)));
  };
  return (
    <div className="stepper">
      <button type="button" className="stepper-btn" onClick={() => bump(-1)} aria-label={`${ariaLabel || 'Valeur'} −`}>−</button>
      <input
        type="number"
        inputMode={decimal ? 'decimal' : 'numeric'}
        className="set-input"
        aria-label={ariaLabel}
        placeholder={placeholder}
        min={min}
        step={decimal ? 0.5 : 1}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value === '' ? null : (decimal ? parseFloat(e.target.value) : parseInt(e.target.value, 10)))}
      />
      <button type="button" className="stepper-btn" onClick={() => bump(1)} aria-label={`${ariaLabel || 'Valeur'} +`}>+</button>
    </div>
  );
}

const HEAT_COLORS = ['var(--bg3)', 'rgba(16,185,129,0.45)', 'var(--accent)'];

export function Heatmap({ grid, size = 14 }) {
  return (
    <div className="heatmap">
      <div className="heatmap-days">
        {['L', '', 'M', '', 'V', '', 'D'].map((d, i) => <div key={i} style={{ height: size }}>{d}</div>)}
      </div>
      {grid.map((week, wi) => (
        <div key={wi} className="heatmap-col">
          {week.map((day, di) => (
            <div
              key={di}
              className="heatmap-cell"
              title={day ? `${day.date} — ${day.count} séance${day.count > 1 ? 's' : ''}` : ''}
              style={{
                width: size,
                height: size,
                background: day ? HEAT_COLORS[Math.min(day.count, 2)] : 'transparent',
                border: day && day.count === 0 ? '1px solid var(--border)' : 'none',
              }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}
